import { AdornisRouter } from '@adornis/base/client/a-router';
import { logger } from '@adornis/base/logging';
import { ChemistryLitElement } from '@adornis/chemistry/chemistry-lit-element';
import { BehaviorController } from '@adornis/chemistry/controllers/behavior-controller';
import { SizeController } from '@adornis/chemistry/controllers/size-controller';
import { css } from '@adornis/chemistry/directives/css';
import { ElementsDialog } from '@adornis/dialog/x-dialog';
import { EntityValidator } from '@adornis/forms/entity-validator';
import '@adornis/forms/x-form';
import '@adornis/germanium/components/ge-button';
import GermaniumDialog from '@adornis/germanium/components/ge-dialog';
import '@adornis/germanium/components/ge-input';
import { GermaniumInput } from '@adornis/germanium/components/ge-input';
import '@adornis/germanium/components/ge-link';
import '@adornis/germanium/components/ge-section';
import '@adornis/germanium/components/ge-toggle-button';
import { ValidationError } from '@adornis/validation/errors/ValidationError';
import { WTGUser } from 'db/wtg-user';
import { html } from 'lit';
import { ref } from 'lit-html/directives/ref.js';
import { customElement, property, state } from 'lit/decorators.js';
import { firstValueFrom, Subject } from 'rxjs';
import { injectColorfulStuffForForms } from './inject-colorful-stuff-for-forms';

@customElement('wtg-signup')
export class WTGSignup extends ChemistryLitElement {
  // styles = () => ({ ':host': { backgroundColor: 'white' } });

  @property({ reflect: true, type: Boolean }) inviteMailSent = false;

  @state() size = new SizeController(this);

  private user = new WTGUser({ email: AdornisRouter.get().queryParams.getOnce('email') || '' });

  public render() {
    return html`
      <x-flex crossaxis-center ${css({ width: '100%', padding: `80px 0px`, position: 'relative' })}>
        ${injectColorfulStuffForForms(this.size.width)} ${this.formBox()}
      </x-flex>
    `;
  }

  @state() hasGermanUniEmail = true;

  private formBox() {
    if (this.inviteMailSent)
      return html`
        <ge-section>
          <x-flex crossaxis-center ${css({ width: '100%', maxWidth: '700px', margin: '0 auto' })} space="xl">
            <ge-h1 ${css({ textAlign: 'center' })}> Thanks :) </ge-h1>
            <ge-h3 ${css({ textAlign: 'center' })}>
              We've sent you an email with an activation link. Please click on the link to complete your registration.
            </ge-h3>

            <ge-h4 ${css({ textAlign: 'center', color: '#617182' })}>
              You didn't get an activation email? Check your spam folder or:
            </ge-h4>

            <ge-button
              mode="action"
              big
              @click=${() => this.dispatchEvent(new CustomEvent('done', { bubbles: true, composed: true }))}
            >
              SEND AGAIN
            </ge-button>
          </x-flex>
        </ge-section>
      `;

    return html`
      <x-flex ${css({ maxWidth: '690px', padding: `0px ${this.spacing.md}` })}>
        <x-form
          id="form"
          editable
          .validator=${EntityValidator.fromEntity(WTGUser)}
          id="student-form"
          .document=${this.user}
          @success=${async e => {
            const user = e.detail as WTGUser;

            const waitFor = new Subject<void>();
            ElementsDialog.waitFor(firstValueFrom(waitFor));

            try {
              const password = this.shadowRoot?.getElementById('pw') as BehaviorController<string>;
              if (!password) throw new Error('Bitte ein Passwort angeben.');

              if (this._pwRepeat?.value.value !== password.value.value) throw new Error('Passwörter sind nicht gleich');

              const userID = await WTGUser.registerWTGUser(user!.username, user!.email, password.value.value)('');

              waitFor.next();

              if (userID) this.inviteMailSent = true;
            } catch (error: any) {
              waitFor.next();

              if (error.message?.includes('Email is already in use')) {
                this.errorMessage = 'Email is already in use';
              } else if (error.message?.includes('Error: requestRegisterUser(): Username is already in use'))
                this.errorMessage = error.message;
              else if (error.message?.includes('Bitte ein Passwort angeben.')) this.errorMessage = error.message;
              else this.errorMessage = error.message;
            }
          }}
          @error=${(e: CustomEvent<ValidationError>) => {
            logger.error('FORM ERROR', e);

            GermaniumDialog.alert(e.detail.message);
          }}
        >
          <x-flex space="xl">
            <ge-h1 ${css({ textAlign: 'center', fontWeight: '700' })}> Register for free </ge-h1>

            <x-flex crossaxis-center space="lg">
              <ge-h4 primary ${css({ textAlign: 'center', fontWeight: '700' })}>
                Do you hold an email address of a German university?
              </ge-h4>

              <ge-toggle-button
                .value=${this.hasGermanUniEmail}
                @value-changed=${e => {
                  if (e.detail.value === this.hasGermanUniEmail) return;
                  this.hasGermanUniEmail = e.detail.value;
                }}
              ></ge-toggle-button>
            </x-flex>

            ${this.hasGermanUniEmail ? this.hasUniEmailForm() : this.hasNoUniEmailForm()}

            <ge-text ${css({ textAlign: 'center', paddingTop: this.spacing.md, color: '#617182' })}>
              By continuing, you agree to our <a href="/" ${css({ color: '#617182' })}> Terms and Conditions </a> and
              our <a href="/" ${css({ color: '#617182' })}> Privacy Policy </a>.
            </ge-text>
          </x-flex>
        </x-form>
      </x-flex>
    `;
  }

  private _pwRepeat?: GermaniumInput;

  private commonRegisterFields() {
    return html`
      <x-flex space="sm">
        <ge-text> E-Mail </ge-text>
        <ge-input
          ${ref(email => (this._email = email))}
          a-form-key="email"
          label="E-Mail"
          placeholder="E-Mail"
          type="email"
        ></ge-input>
      </x-flex>

      <x-flex space="sm">
        <ge-text> Username </ge-text>
        <ge-input
          ${ref(username => (this._username = username))}
          a-form-key="username"
          placeholder-mode="hide"
          label="Username"
          placeholder="Username"
          type="email"
        ></ge-input>
      </x-flex>

      <x-flex space="sm">
        <ge-text> Password </ge-text>
        <ge-input placeholder="Password" type="password" id="pw"></ge-input>
      </x-flex>

      <x-flex space="sm">
        <ge-text> Repeat Password </ge-text>
        <ge-input
          ${ref(pwRepeat => (this._pwRepeat = pwRepeat as GermaniumInput | undefined))}
          placeholder="Password"
          type="password"
        ></ge-input>
      </x-flex>

      <ge-text ${css({ color: this.colors.accent })}> ${this.errorMessage} </ge-text>
    `;
  }

  @state() errorMessage = '';

  private hasNoUniEmailForm() {
    return html`
      <x-flex ${css({ width: '100%' })} crossaxis-center space="md">
        <ge-text center> Get a basic account to obtain support from the community </ge-text>

        <x-flex space="sm" ${css({ width: '100%', padding: `0px ${this.spacing.xl}` })}>
          ${this.commonRegisterFields()}

          <ge-button big validate mode="action"> REGISTER </ge-button>
        </x-flex>
      </x-flex>
    `;
  }

  private hasUniEmailForm() {
    return html`
      <ge-text center>
        Verified accounts demostrate that you are a real person. As verified user you get exclusive access to events,
        job offers and much more!
      </ge-text>

      <x-flex space="lg" ${css({ padding: `0px ${this.spacing.xl}` })}>
        <x-flex space="sm"> ${this.commonRegisterFields()} </x-flex>

        <x-flex horizontal space-between>
          <x-flex space="sm">
            <ge-text> Already registered? </ge-text>

            <ge-text>
              <a href="/login" ${css({ color: '#00455d', fontWeight: '600', textDecorationLine: 'underline' })}>
                Login here!
              </a>
            </ge-text>
          </x-flex>
          <ge-button validate mode="action" ${css({ boxShadow: 'none' })}> REGISTER </ge-button>
        </x-flex>
      </x-flex>

      <x-flex space="xl" ${css({ padding: `0px 50px` })}>
        <x-flex horizontal space="md" crossaxis-center>
          <x-flex flex ${css({ height: '1px', backgroundColor: 'black' })}> </x-flex>
          <ge-text> Or </ge-text>
          <x-flex flex ${css({ height: '1px', backgroundColor: 'black' })}> </x-flex>
        </x-flex>
      </x-flex>
      <x-flex space="md">
        <ge-text primary ${css({ textAlign: 'center' })}>
          If you don’t have an email address of a German university but you are working in Germany, you can request
          identity verification. *Fees could apply
        </ge-text>

        <x-flex space="xl" ${css({ padding: `0px ${this.spacing.xl}` })}>
          <x-flex horizontal space="md" crossaxis-end>
            <x-flex flex space="sm">
              <ge-text> Email </ge-text>
              <ge-input flex a-form-key="email" placeholder="E-Mail" type="email"></ge-input>
            </x-flex>
            <ge-button ${css({ height: '40px' })} mode="outline" tone="error"> SEND </ge-button>
          </x-flex>
        </x-flex>
      </x-flex>
    `;
  }
}
